import { fr } from "../i18n/fr.js";
import { escape } from "./format.js";

export const COMPACT_QUERY = "(max-width: 60rem), (max-height: 36rem)";
export type MobilePane = "tools" | "bodies" | "inspector";
export interface MobileHandle {
  readonly compact: () => boolean;
  readonly isOpen: () => boolean;
  readonly open: (pane: MobilePane) => void;
  readonly close: () => void;
  readonly report: (message: string) => void;
  readonly detach: () => void;
}

/** Les mêmes panneaux passent dans un dialogue tactile, sans dupliquer leurs contrôles. */
export function mountMobile(root: Element): MobileHandle {
  const media = window.matchMedia(COMPACT_QUERY);
  const selectors = { tools: ".verlet-tools", bodies: "[data-entities]", inspector: "[data-inspector]" } as const;
  const titles = { tools: fr.mobileTools, bodies: fr.mobileBodies, inspector: fr.mobileInspector };
  const panes = new Map<MobilePane, { node: HTMLElement; home: Comment }>();
  for (const key of Object.keys(selectors) as MobilePane[]) {
    const node = root.querySelector<HTMLElement>(selectors[key]);
    if (node === null) throw new Error(`panneau absent : ${key}`);
    const home = document.createComment(key);
    node.after(home);
    panes.set(key, { node, home });
  }
  const dialog = document.createElement("dialog");
  dialog.className = "verlet-mobile-dialog";
  dialog.setAttribute("aria-labelledby", "mobile-sheet-title");
  dialog.innerHTML = `<header class="verlet-mobile-dialog__head"><h2 id="mobile-sheet-title"></h2><button type="button" class="verlet-chip" data-mobile-close>${escape(fr.mobileClose)}</button></header><p class="verlet-mobile-dialog__status" data-mobile-status role="alert" hidden></p><div class="verlet-mobile-dialog__content"></div>`;
  root.append(dialog);
  const content = dialog.querySelector<HTMLElement>(".verlet-mobile-dialog__content")!;
  const status = dialog.querySelector<HTMLElement>("[data-mobile-status]")!;
  const head = dialog.querySelector<HTMLElement>("header")!;
  let current: MobilePane | undefined;
  let returnFocus: HTMLElement | undefined;

  function restore(): void {
    if (current === undefined) return;
    const pane = panes.get(current)!;
    pane.home.before(pane.node);
    current = undefined;
    for (const trigger of root.querySelectorAll("[data-mobile-pane]")) trigger.setAttribute("aria-expanded", "false");
  }
  function close(): void {
    restore();
    if (dialog.open) dialog.close();
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  }
  function open(pane: MobilePane): void {
    if (!media.matches) return;
    if (!dialog.open) returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    restore();
    status.hidden = true;
    status.textContent = "";
    current = pane;
    content.append(panes.get(pane)!.node);
    content.scrollTop = 0;
    dialog.querySelector("h2")!.textContent = titles[pane];
    for (const trigger of root.querySelectorAll<HTMLElement>("[data-mobile-pane]")) {
      trigger.setAttribute("aria-expanded", String(trigger.dataset["mobilePane"] === pane));
    }
    if (!dialog.open) dialog.showModal();
    dialog.querySelector<HTMLElement>("[data-mobile-close]")!.focus({ preventScroll: true });
  }
  function report(message: string): void {
    if (!dialog.open) return;
    status.textContent = message;
    status.hidden = false;
  }
  const onClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-mobile-close]") !== null) close();
    else if (target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right || event.clientY > rect.bottom) close();
    }
  };
  const onCancel = (event: Event): void => { event.preventDefault(); close(); };
  let dragStart: number | undefined;
  const onDown = (event: PointerEvent): void => {
    if (!(event.target as HTMLElement).closest("button")) {
      dragStart = event.clientY;
      head.setPointerCapture(event.pointerId);
    }
  };
  const onUp = (event: PointerEvent): void => {
    if (dragStart !== undefined && event.clientY - dragStart > 65) close();
    dragStart = undefined;
  };
  const onMedia = (): void => { if (!media.matches) close(); };
  const onViewport = (): void => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    dialog.style.setProperty("--sheet-visible-height", `${viewport.height}px`);
    dialog.style.setProperty("--sheet-keyboard", `${Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)}px`);
  };
  dialog.addEventListener("click", onClick);
  dialog.addEventListener("cancel", onCancel);
  dialog.addEventListener("close", () => { if (!dialog.open) restore(); });
  head.addEventListener("pointerdown", onDown);
  dialog.addEventListener("pointerup", onUp);
  media.addEventListener("change", onMedia);
  window.visualViewport?.addEventListener("resize", onViewport);
  window.visualViewport?.addEventListener("scroll", onViewport);
  onViewport();
  return { compact: () => media.matches, isOpen: () => dialog.open, open, close, report, detach: () => {
    close();
    dialog.remove();
    for (const pane of panes.values()) pane.home.remove();
    media.removeEventListener("change", onMedia);
    window.visualViewport?.removeEventListener("resize", onViewport);
    window.visualViewport?.removeEventListener("scroll", onViewport);
  } };
}
